import * as React from 'react'
import { type Editor } from '@tiptap/react'
import * as Toolbar from '@radix-ui/react-toolbar'
import {
  CodeIcon,
  FontBoldIcon,
  FontItalicIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from '@radix-ui/react-icons'
import { cva } from 'class-variance-authority'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/sotano-kit/primitives/tooltip'
import {
  isValidImageType,
  VALID_IMAGE_MIME_TYPES,
} from '@basehub/data/replicache/schema/block/valid-mime-types'
import { toast } from 'sonner'
import { startImageUpload } from '../../plugins/upload-image'
import {
  ComponentBlock,
  isComponentBlock,
  isRichTextBlock,
  RichTextBlock,
} from '@basehub/data/replicache/index'
import {
  HierarchyDropdownMenu,
  hierarchyList,
} from './components/hierarchy-dropdown'
import { SelectInstanceBlock } from './components/basehub-instance-selector'
import { InsertLinkAction } from './components/insert-link'
import { TableActions } from './components/table-actions'

import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { HighlightIcon } from '~/components/icons/remark-icon'
import {
  optionToNewOptions,
  richTextOptions,
} from '@basehub/tiptap-api-utils/options'
import { BaseHubInlineBlock } from '@basehub/tiptap-api-utils'
import { KbdIcon } from '~/components/icons/kbd'
import { SuperscriptIcon } from '~/components/icons/superscript-icon'
import { SubscriptIcon } from '~/components/icons/subscript-icon'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { getAllowedFormatting } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/rich-text/_utils'

export function MenuBar({
  editor,
  block: richText,
  path,
  isShowingBubbleMenu,
}: {
  editor: Editor
  block: RichTextBlock
  path: string
  isShowingBubbleMenu: boolean
}) {
  const imageInputRef = React.useRef<HTMLInputElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { data } = useRepoReplicache()
  const teamId = data?.team.id
  const repoId = data?.repo.id

  const formattedAllowedNodes = React.useMemo(() => {
    return getAllowedFormatting(richText.constraints).map(optionToNewOptions)
  }, [richText.constraints])

  const [validComponents, setValidComponents] = React.useState<
    ComponentBlock[]
  >([])

  const blockTreeManager = useWorkingTreeStore((s) => s.treeRef)

  React.useEffect(() => {
    const validComponents: ComponentBlock[] = []
    if (!blockTreeManager.current) return
    const richTextNode = blockTreeManager.current.getNode(richText.id)
    if (!richTextNode || !isRichTextBlock(richTextNode.value)) return

    const allowedComponents = richTextNode.value.constraints.allowedComponents

    allowedComponents?.forEach((componentId) => {
      if (!blockTreeManager.current) return
      const component = blockTreeManager.current.getNode(componentId)?.value
      if (component && isComponentBlock(component)) {
        validComponents.push(component)
      }
    })
    setValidComponents(validComponents)
  }, [blockTreeManager, richText.id, setValidComponents])

  const activeHierarchy = hierarchyList.find((item) => item.isActive(editor))
  // ?? hierarchyList.find((item) => item.name === 'paragraph')!

  const allowedHierarchy = hierarchyList.filter((item) => {
    if (item.name === activeHierarchy?.name) return true
    return (
      !richTextOptions.includes(item.name) ||
      formattedAllowedNodes.includes(item.name)
    )
  })

  const disabledRecord = {
    hierarchy: !activeHierarchy,
    bold:
      (!editor.can().chain().focus().toggleBold().run() ||
        !formattedAllowedNodes.includes('bold')) &&
      // if it's active, we can still toggle it off
      !editor.isActive('bold'),
    italic:
      (!editor.can().chain().focus().toggleItalic().run() ||
        !formattedAllowedNodes.includes('italic')) &&
      !editor.isActive('italic'),
    strikethrough:
      (!editor.can().chain().focus().toggleStrike().run() ||
        !formattedAllowedNodes.includes('strike')) &&
      !editor.isActive('strike'),
    code:
      (!editor.can().chain().focus().toggleCode().run() ||
        !formattedAllowedNodes.includes('code')) &&
      !editor.isActive('code'),
    kbd:
      (!editor.can().chain().focus().toggleKbd().run() ||
        !formattedAllowedNodes.includes('kbd')) &&
      !editor.isActive('kbd'),
    highlight:
      (!editor.can().chain().focus().toggleHighlight().run() ||
        !formattedAllowedNodes.includes('highlight')) &&
      !editor.isActive('highlight'),
    superscript:
      (!editor.can().chain().focus().toggleSuperscript().run() ||
        !formattedAllowedNodes.includes('superscript')) &&
      !editor.isActive('superscript'),
    subscript:
      (!editor.can().chain().focus().toggleSubscript().run() ||
        !formattedAllowedNodes.includes('subscript')) &&
      !editor.isActive('subscript'),
    link: !formattedAllowedNodes.includes('link') && !editor.isActive('link'),
    caption: !(editor.isActive('image') || editor.isActive('video')),
    table: !editor.isActive('table'),
    instance:
      (validComponents.length === 0 ||
        editor.can().setMark(BaseHubInlineBlock.name) === false) &&
      !editor.isActive(BaseHubInlineBlock.name),
    underline:
      !formattedAllowedNodes.includes('underline') &&
      !editor.isActive('underline'),
  }

  const areAllDisabled = Object.values(disabledRecord).every((v) => v)

  if (areAllDisabled) {
    return (
      <p className="text-xs text-dark-muted px-0.5">No actions to perform</p>
    )
  }

  return (
    <>
      <Toolbar.Root
        ref={containerRef}
        className="w-fit items-center rounded-md flex"
        aria-label="Formatting options"
        data-bubble-menu-rich-text={isShowingBubbleMenu}
      >
        {!disabledRecord.hierarchy && activeHierarchy && (
          <Toolbar.ToggleGroup type="single" aria-label="List">
            {/* Hierarchy items (Paragraph, Heading 1 2 3, Code block. etc...) */}
            <HierarchyDropdownMenu
              blockId={richText.id}
              activeHierarchy={activeHierarchy}
              allowedHierarchy={allowedHierarchy}
              editor={editor}
              containerElement={containerRef}
            />
          </Toolbar.ToggleGroup>
        )}
        {/* Marks: Italic, Bold, Code, Strike, Link, Superscript, Subscript */}
        {(!disabledRecord.bold ||
          !disabledRecord.italic ||
          !disabledRecord.strikethrough ||
          !disabledRecord.code ||
          !disabledRecord.kbd ||
          !disabledRecord.highlight ||
          !disabledRecord.superscript ||
          !disabledRecord.subscript) && (
          <>
            {!disabledRecord.hierarchy && (
              <Toolbar.Separator className="mx-2 h-4 w-px flex-shrink-0 bg-dark-elevated" />
            )}
            <Toolbar.ToggleGroup
              type="multiple"
              aria-label="Text formatting"
              className="space-x-1"
            >
              {!disabledRecord.bold && (
                <TooltipWrapper
                  trigger={
                    <Toolbar.ToggleItem
                      className={$toggleItem({
                        active: editor.isActive('bold'),
                      })}
                      value="bold"
                      aria-label="Bold"
                      onPointerDown={(e) => {
                        e.preventDefault()
                        editor.chain().focus().toggleBold().run()
                      }}
                    >
                      <FontBoldIcon />
                    </Toolbar.ToggleItem>
                  }
                  content="Bold"
                />
              )}
              {!disabledRecord.italic && (
                <TooltipWrapper
                  trigger={
                    <Toolbar.ToggleItem
                      className={$toggleItem({
                        active: editor.isActive('italic'),
                      })}
                      value="italic"
                      aria-label="Italic"
                      onPointerDown={(e) => {
                        e.preventDefault()
                        editor.chain().focus().toggleItalic().run()
                      }}
                    >
                      <FontItalicIcon />
                    </Toolbar.ToggleItem>
                  }
                  content="Italic"
                />
              )}
              {!disabledRecord.strikethrough && (
                <TooltipWrapper
                  trigger={
                    <Toolbar.ToggleItem
                      className={$toggleItem({
                        active: editor.isActive('strike'),
                      })}
                      value="strikethrough"
                      aria-label="Strike through"
                      onPointerDown={(e) => {
                        e.preventDefault()
                        editor.chain().focus().toggleStrike().run()
                      }}
                    >
                      <StrikethroughIcon />
                    </Toolbar.ToggleItem>
                  }
                  content="Strike through"
                />
              )}
              {!disabledRecord.underline && (
                <TooltipWrapper
                  trigger={
                    <Toolbar.ToggleItem
                      className={$toggleItem({
                        active: editor.isActive('underline'),
                      })}
                      value="underline"
                      aria-label="Underline"
                      onPointerDown={(e) => {
                        e.preventDefault()
                        editor.chain().focus().toggleUnderline().run()
                      }}
                    >
                      <UnderlineIcon />
                    </Toolbar.ToggleItem>
                  }
                  content="Underline"
                />
              )}
              {!disabledRecord.code && (
                <TooltipWrapper
                  trigger={
                    <Toolbar.ToggleItem
                      className={$toggleItem({
                        active: editor.isActive('code'),
                      })}
                      value="code"
                      aria-label="Code"
                      onPointerDown={(e) => {
                        e.preventDefault()
                        editor.chain().focus().toggleCode().run()
                      }}
                    >
                      <CodeIcon />
                    </Toolbar.ToggleItem>
                  }
                  content="Code"
                />
              )}
              {!disabledRecord.kbd && (
                <TooltipWrapper
                  trigger={
                    <Toolbar.ToggleItem
                      className={$toggleItem({
                        active: editor.isActive('kbd'),
                      })}
                      value="kbd"
                      aria-label="Kbd"
                      onPointerDown={(e) => {
                        e.preventDefault()
                        editor.chain().focus().toggleKbd().run()
                      }}
                    >
                      <KbdIcon />
                    </Toolbar.ToggleItem>
                  }
                  content="Kbd"
                />
              )}
              {!disabledRecord.highlight && (
                <TooltipWrapper
                  trigger={
                    <Toolbar.ToggleItem
                      className={$toggleItem({
                        active: editor.isActive('highlight'),
                      })}
                      value="highlight"
                      aria-label="Highlight"
                      onPointerDown={(e) => {
                        e.preventDefault()
                        editor.chain().focus().toggleHighlight().run()
                      }}
                    >
                      <HighlightIcon className="size-4" />
                    </Toolbar.ToggleItem>
                  }
                  content="Highlight"
                />
              )}
              {!disabledRecord.superscript && (
                <TooltipWrapper
                  trigger={
                    <Toolbar.ToggleItem
                      className={$toggleItem({
                        active: editor.isActive('superscript'),
                      })}
                      value="superscript"
                      disabled={editor.isActive('subscript')}
                      aria-label="Superscript"
                      onPointerDown={(e) => {
                        e.preventDefault()
                        editor.chain().focus().toggleSuperscript().run()
                      }}
                    >
                      <SuperscriptIcon className="size-4" />
                    </Toolbar.ToggleItem>
                  }
                  content="Superscript"
                />
              )}
              {!disabledRecord.subscript && (
                <TooltipWrapper
                  trigger={
                    <Toolbar.ToggleItem
                      className={$toggleItem({
                        active: editor.isActive('subscript'),
                      })}
                      value="subscript"
                      aria-label="Subscript"
                      disabled={editor.isActive('superscript')}
                      onPointerDown={(e) => {
                        e.preventDefault()
                        editor.chain().focus().toggleSubscript().run()
                      }}
                    >
                      <SubscriptIcon className="size-4" />
                    </Toolbar.ToggleItem>
                  }
                  content="Subscript"
                />
              )}
            </Toolbar.ToggleGroup>
          </>
        )}
        {(!disabledRecord.link ||
          !disabledRecord.caption ||
          !disabledRecord.instance ||
          !disabledRecord.table) && (
          <>
            {(!disabledRecord.hierarchy ||
              !disabledRecord.bold ||
              !disabledRecord.italic ||
              !disabledRecord.strikethrough ||
              !disabledRecord.code ||
              !disabledRecord.kbd ||
              !disabledRecord.highlight) && (
              <Toolbar.Separator className="mx-2 h-4 w-px flex-shrink-0 bg-dark-elevated" />
            )}
            {/* Custom behaviors and elements */}
            <Toolbar.ToggleGroup
              type="single"
              aria-label="Component"
              className="flex items-center space-x-1"
            >
              {!disabledRecord.link && (
                <InsertLinkAction
                  block={richText}
                  editor={editor}
                  containerRef={containerRef}
                  disabled={
                    !formattedAllowedNodes.includes('link') &&
                    !editor.isActive('link')
                  }
                />
              )}
              {/* Select custom block in BaseHub */}
              {!disabledRecord.instance && (
                <SelectInstanceBlock
                  editor={editor}
                  block={richText}
                  path={path}
                  validComponents={validComponents}
                  containerElement={containerRef}
                  isActive={editor.isActive(BaseHubInlineBlock.name)}
                />
              )}
              {/* Table actions */}
              {!disabledRecord.table && (
                <TableActions
                  editor={editor}
                  containerElement={containerRef}
                  blockId={richText.id}
                />
              )}
            </Toolbar.ToggleGroup>
          </>
        )}
      </Toolbar.Root>

      {/* Hidden input to upload images */}
      <input
        className="hidden"
        ref={imageInputRef}
        type="file"
        accept={VALID_IMAGE_MIME_TYPES.join(',')}
        multiple
        onChange={(e) => {
          if (!teamId || !repoId || !e.target.files?.length) return
          const validFiles = Array.from(e.target.files).filter((file) =>
            isValidImageType(file.type)
          )

          if (!validFiles) {
            return toast.error('Invalid image type')
          }

          const pos = editor.view.state.selection.from

          validFiles.forEach((file) => {
            startImageUpload(file, editor.view, pos, { teamId, repoId })
          })
        }}
      />
    </>
  )
}

export const $toggleItem = cva(
  'flex-shrink-0 disabled:opacity-30 cursor-default outline-dark-control-base outline-1 focus-visible:outline flex-grow-0 h-8 w-8 rounded text-lg p-1.5 inline-flex items-center justify-center leading-none',
  {
    variants: {
      active: {
        true: 'bg-dark-control-base/10 text-dark-control-base',
        false: 'hover:bg-dark-shade-hover text-dark-dim',
      },
    },
  }
)

export const TooltipWrapper = ({
  trigger,
  content,
}: {
  trigger: React.ReactNode
  content: string
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  )
}
